/**
 * Where the web app is running (android-app ticket 04, spec D7). One place for the question "is this the Android app's
 * web view?". Capacitor is not a dependency of this build: the native shell injects `window.Capacitor` before the page
 * loads, so this reads that global, and it is false in every browser. Nothing in `frontend/` imports `@capacitor/*`.
 */
export function isNativeApp(): boolean {
  try {
    const c = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
    return c?.isNativePlatform?.() === true;
  } catch {
    return false;
  }
}

/**
 * True only in the Android app's web view (android-polish ticket 01). The app's own look (`src/android/`, the
 * `html.android-app` class) keys on this, never on a width: a phone browser shares every width with the app.
 */
export function isAndroidApp(): boolean {
  try {
    const c = (window as unknown as { Capacitor?: { getPlatform?: () => string } }).Capacitor;
    return isNativeApp() && c?.getPlatform?.() === 'android';
  } catch {
    return false;
  }
}

/** `isAndroidApp()` for a component. The bridge is there before the page loads and never leaves, so it is read once. */
export function useIsAndroidApp(): boolean {
  return ANDROID_APP;
}

const ANDROID_APP = typeof window !== 'undefined' && isAndroidApp();

/**
 * True inside the Android app when its build fills employer forms (review fix 1; build switch `CJ_APP_FILL_ENABLED`). The
 * shell's fill host sets `window.cjAppFill` at document start (`mobile/src/fill/install.ts`, `announceFill`). Off, the app
 * fills nothing: the kit shows the copy-paste path and the Job Queue story says nothing about filling, as on phone web.
 */
export function appFillsForms(): boolean {
  try {
    return isNativeApp() && (window as unknown as { cjAppFill?: unknown }).cjAppFill === true;
  } catch {
    return false;
  }
}

/**
 * The first element that matches `selector` and is drawn on the screen (it has a width and crosses the window), else the first
 * match, else null. In the Android app the rail stays in the page, hidden, while the tab bar is drawn, so one selector can match
 * both, and the one on the screen is the one a swipe lands on (`DeckScreen`, `useSwipe`).
 */
export function onScreenFirst<T extends Element = HTMLElement>(selector: string): T | null {
  const all = [...document.querySelectorAll<T>(selector)];
  const drawn = (el: T) => { const b = el.getBoundingClientRect(); return b.width > 0 && b.right > 0 && b.left < window.innerWidth; };
  return all.find(drawn) ?? all[0] ?? null;
}

let baseline = { w: 0, h: 0 };

/**
 * Remember how tall the window is while no field has focus (so no keyboard is open). In the Android app the web view
 * itself shrinks when the keyboard opens, so `innerHeight` falls with it and only this remembered height shows the
 * keyboard. A new width is a turn of the phone: the old height means nothing. A browser does not use it.
 */
export function noteWindowHeight(): void {
  if (!isNativeApp()) return;
  if (baseline.w !== window.innerWidth) baseline = { w: window.innerWidth, h: 0 };
  baseline.h = Math.max(baseline.h, window.innerHeight);
}

/**
 * True while the on-screen keyboard covers the page: a field has focus and the visible height is clearly short of the
 * full one (ux-bug-pass-8 ticket 05 and the app's tab bar). In a browser the full height is `innerHeight`, because the
 * layout viewport keeps its size while the keyboard shrinks the visual one (a hardware keyboard shrinks nothing, so
 * the bar stays; without `visualViewport` a focused field alone counts). A browser's answer is what it always was.
 * In the app the full height is the one `noteWindowHeight` remembered.
 */
export function keyboardOpen(): boolean {
  const a = document.activeElement as HTMLElement | null;
  const typing = !!a && (a.isContentEditable || a.tagName === 'INPUT' || a.tagName === 'TEXTAREA' || a.tagName === 'SELECT');
  if (!typing) return false;
  const vv = window.visualViewport;
  if (!vv) return true;
  if (!isNativeApp()) return vv.height < window.innerHeight - 100;
  return vv.height < Math.max(baseline.h, window.innerHeight) - 100;
}
